import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';

// COMPONENTS
import {
  Box,
  Link,
  Typography,
  CardArt,
  Icon,
  Element,
  FontType,
  Color,
  Size,
  IconBulletedList,
  VAlign
} from '@americanexpress/aqx-components';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import LazyLoad from 'src/universal/components/DTM/LazyScrollMetricTracker';
import YourCardBadge from 'src/universal/components/YourCardBadge';
import { showG162Banner } from 'src/universal/components/Experiments/G162/redux';
import PageBreakBanner from '../PageBreakBanner';
import AmazonDisclaimer from '../AmazonDisclaimer';

// SELECTORS
import {
  getAnnualFeeLabel,
  makeGetAnnualFeeDisclaimer,
  getLabels,
  makeGetFeaturedBenefits,
  getDtm
} from './redux';

// UTILS
import {
  mapUrlWithDtmLinkQuery,
  sendDTMClickEvent
} from 'service_modules/models/dtm-tagging';
import { processTncUrl } from 'service_modules/models/shared/cross-model-helper';

// STYLES
import styles from '../../__styles__/listStyles.scss?modules';
import driveToPdpStyles from 'core_modules/cardTileDriveToPdp/__styles__/index.scss?modules';

const ListTile = ({
  isInCurrentFilter,
  card,
  cardArtUrl,
  annualFeeLabel,
  feeDisclaimer,
  labels,
  dtm,
  cardEep,
  featuredBenefits,
  showG162,
  filteredCards
}) => {
  const { cardHeadline, annualFee, learnMoreButton, tncLinks, shortName } = card;
  const annualFeeText = (annualFee ?? '').replace(' Annual Fee', '');
  const annualFeeLabelText = annualFeeLabel?.text;
  const cardURL = learnMoreButton?.url ?? '';
  const pdpUrl = mapUrlWithDtmLinkQuery(cardURL, listViewClickEvent);
  const { offerTerms, ratesFees } = tncLinks;
  const { listOfferTerms, listFees, listClick } = dtm;
  const listViewClickEvent = {
    ...listClick,
    eventAsset: `${listClick?.eventAsset}${card.dtmProductName}`,
    tag: `${listClick?.tag}${card.dtmProductName}`
  };

  const displayG162Banner = showG162 && card.shortName === filteredCards[2];

  return (
    <React.Fragment>
      <div style={{ display: isInCurrentFilter ? 'block' : 'none' }}>
        <Box
          $border={true}
          className={'mobileCardTile'}
          theme={{
            marginBottom: 10,
            rounded: Size.XS,
            borderColor: Color.Gray03
          }}
        >
          <Link
            href={pdpUrl}
            className={driveToPdpStyles.topButtonWrapper}
            onClick={() => sendDTMClickEvent(listViewClickEvent)}
          >
            <div>
              <div className={clsx(styles.header, driveToPdpStyles.header)}>
                <LazyLoad offset={200}>
                  <CardArt
                    src={cardArtUrl}
                    alt={card?.cardArtAltText}
                    width="380"
                    height="240"
                  />
                </LazyLoad>
                <h3>
                  <Typography
                    $size="16px"
                    $lineHeight="24px"
                    className="card-title-wrap"
                    $family={FontType.HelveticaBold}
                    theme={{
                      color: Color.DeepBlue,
                      paddingLeft: 20,
                      paddingTop: 5
                    }}
                  >
                    {cardHeadline}
                  </Typography>
                  <YourCardBadge shortName={shortName} />
                </h3>
                <Icon
                  directory="dls"
                  imageName="dls-glyph-right"
                  $size={Size.SMX}
                  theme={{
                    marginLeft: 'auto',
                    color: Color.DeepBlue
                  }}
                />
              </div>
              <div className="annual-fee">
                <Typography
                  $size="15px"
                  $lineHeight="22px"
                  theme={{
                    color: Color.DeepBlue
                  }}
                >
                  <Sanitizer safelySetInnerHTML={annualFeeLabelText} />
                  <Sanitizer safelySetInnerHTML={annualFeeText} class="card-fee" />
                  {feeDisclaimer && (
                    <Sanitizer
                      Component="div"
                      className="fee-disclaimer"
                      safelySetInnerHTML={feeDisclaimer}
                    />
                  )}
                </Typography>
              </div>
            </div>
            <Typography
              $size="15px"
              $lineHeight="22px"
              theme={{
                color: Color.DeepBlue
              }}
            >
              <Sanitizer
                safelySetInnerHTML={labels.featuredBenefitTitle?.text ?? ''}
              />
            </Typography>
            <div className={styles.listWrapper}>
              <IconBulletedList
                data={{
                  bulletPoints: featuredBenefits.map(eachBulletPoint => ({
                    icon: {
                      directory: 'dls',
                      imageName: eachBulletPoint.icon,
                      $size: Size.SM,
                      hiddenToScreenReaders: true
                    },
                    text: eachBulletPoint.text
                  }))
                }}
                $iconAlign={VAlign.Top}
                customization={{ listItem: { paddingLeft: 10 } }}
              />
            </div>
            <Element
              style={{
                borderTop: '1px solid #ECEDEE'
              }}
            />
          </Link>
          <AmazonDisclaimer shortName={card.shortName} />
          <Element className={styles.bottomBox}>
            {offerTerms && (
              <Link
                theme={{
                  color: Color.BrightBlue,
                  textDecoration: 'underline',
                  fontType: FontType.HelveticaRegular
                }}
                onClick={() => sendDTMClickEvent({ ...listOfferTerms })}
                style={{ fontSize: '13px' }}
                target="_blank"
                href={processTncUrl(
                  {
                    url: offerTerms.url,
                    urlAppend: offerTerms.urlAppend
                  },
                  cardEep
                )}
              >
                <Sanitizer safelySetInnerHTML={labels?.offerTerms?.text ?? ''} />
              </Link>
            )}
            {ratesFees && (
              <Link
                theme={{
                  color: Color.BrightBlue,
                  textDecoration: 'underline',
                  fontType: FontType.HelveticaRegular
                }}
                onClick={() => sendDTMClickEvent({ ...listFees })}
                target="_blank"
                style={{ fontSize: '13px' }}
                href={processTncUrl(
                  {
                    url: ratesFees.url,
                    urlAppend: ratesFees.urlAppend
                  },
                  cardEep
                )}
              >
                <Sanitizer safelySetInnerHTML={labels?.ratesFees?.text ?? ''} />
              </Link>
            )}
          </Element>
        </Box>
      </div>
      {displayG162Banner && <PageBreakBanner mobile={true} />}
    </React.Fragment>
  );
};

const mapStateToProps = () => {
  const getAnnualFeeDisclaimer = makeGetAnnualFeeDisclaimer();
  const getFeaturedBenefits = makeGetFeaturedBenefits();
  return (state, { shortName }) => ({
    annualFeeLabel: getAnnualFeeLabel(state),
    feeDisclaimer: getAnnualFeeDisclaimer(state, shortName),
    labels: getLabels(state),
    featuredBenefits: getFeaturedBenefits(state, shortName),
    dtm: getDtm(state),
    showG162: showG162Banner(state, state?.filter?.activeFilter)
  });
};

export default connect(mapStateToProps)(ListTile);
